import {
  createInquiryId,
  normalizeOwnerInquiry,
  type OwnerInquiry,
  type OwnerInquiryInput,
  type OwnerInquiryStatus,
} from "@/domain/owner-inquiry";
import { DOG_SALON_SNAPS_PRODUCT } from "@/domain/product";
import { INQUIRY_STORE, idbReq, openAppDb } from "@/lib/app-db";

export async function listOwnerInquiries(): Promise<OwnerInquiry[]> {
  const db = await openAppDb();
  try {
    const rows = await idbReq(
      db.transaction(INQUIRY_STORE, "readonly").objectStore(INQUIRY_STORE).getAll(),
    );
    return ((rows as OwnerInquiry[]) ?? [])
      .map((r) => normalizeOwnerInquiry(r))
      .sort((a, b) => b.createdAt - a.createdAt);
  } finally {
    db.close();
  }
}

export async function saveOwnerInquiry(input: OwnerInquiryInput): Promise<OwnerInquiry> {
  const record = normalizeOwnerInquiry({
    id: createInquiryId(),
    topic: input.topic,
    body: input.body.trim(),
    contact: input.contact.trim(),
    images: input.images ?? [],
    createdAt: Date.now(),
    status: "queued",
    assignee: DOG_SALON_SNAPS_PRODUCT.developerNameJa,
  });
  const db = await openAppDb();
  try {
    await idbReq(
      db.transaction(INQUIRY_STORE, "readwrite").objectStore(INQUIRY_STORE).put(record),
    );
    return record;
  } finally {
    db.close();
  }
}

export async function setOwnerInquiryStatus(
  id: string,
  status: OwnerInquiryStatus,
): Promise<OwnerInquiry | null> {
  const db = await openAppDb();
  try {
    const store = db.transaction(INQUIRY_STORE, "readwrite").objectStore(INQUIRY_STORE);
    const raw = await idbReq(store.get(id));
    if (!raw) return null;
    const next = normalizeOwnerInquiry({
      ...(raw as OwnerInquiry),
      status,
    });
    await idbReq(store.put(next));
    return next;
  } finally {
    db.close();
  }
}
